import { ArchivedCompanyNotice } from '@/components/archived-company-notice';
import { CompanySpaceBreadcrumb } from '@/components/breadcrumbs';
import { type CompanyInfoDraft, CompanyInfoForm } from '@/components/company-info-form';
import { MatricesTab } from '@/components/matrix-grid/matrices-tab';
import { PaliersEditor } from '@/components/paliers-editor';
import {
  type AppliedSetupImport,
  SetupImportDialog,
} from '@/components/setup-import/setup-import-dialog';
import { SingularitySetupEditor } from '@/components/singularity-setup-editor';
import { companiesApi, meApi } from '@/lib/api';
import { canManageCompanies, canMutateCompany } from '@/lib/company-permissions';
import { useEffectiveRole } from '@/lib/effective-role';
import { LOGO_WARNING_MESSAGE, clearLogoWarning, hasLogoWarning } from '@/lib/logo-warning';
import {
  type MatrixGridSearch,
  isDefaultMatrixGridSearch,
  parseMatrixGridSearch,
  readStoredMatrixGridSearch,
  storeMatrixGridSearch,
} from '@/lib/matrix-grid-search';
import { useStoredCompanyLogo } from '@/lib/use-stored-company-logo';
import type {
  Company,
  CompanyWithSetup,
  ResolvedPalier,
  SingularityLevier,
} from '@mss_creator/shared';
import { Alert, Button, Icon } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useBlocker, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

export const Route = createFileRoute('/companies/$companyId')({
  // The Matrices tab's view, sort and filters (issue #16). Every key is
  // optional, so the existing links to this route stay valid without a search.
  validateSearch: (search): MatrixGridSearch => parseMatrixGridSearch(search),
  // Reached without params (breadcrumb, rail, company card): the last settings
  // used for this company come back from localStorage. With params (a shared
  // link, the back button), they win and become the new last settings.
  beforeLoad: ({ params, search }) => {
    if (!isDefaultMatrixGridSearch(search)) {
      storeMatrixGridSearch(params.companyId, search);
      return;
    }
    const stored = readStoredMatrixGridSearch(params.companyId);
    if (!isDefaultMatrixGridSearch(stored)) {
      throw redirect({
        to: '/companies/$companyId',
        params: { companyId: params.companyId },
        search: stored,
        replace: true,
      });
    }
  },
  component: CompanySpacePage,
  staticData: { breadcrumb: CompanySpaceBreadcrumb },
});

type TabKey = 'grid' | 'settings';

/**
 * Rendered once, directly under the tab bar, so it occupies the same spot on
 * both tabs instead of being duplicated between the settings panel and the
 * "Nouvelle matrice" card (which used to carry it as `disabledReason`).
 *
 * It is gated on the *server-computed* `isSetupComplete`, deliberately not on
 * the settings panel's local `setupDraft`: matrix creation is re-checked
 * server-side in `matrices.ts`, so a banner that vanished on an unsaved draft
 * would tell the user they can generate a matrix when the API would still
 * refuse. `handleSave` already invalidates the company query, so it clears
 * itself once the refetch lands.
 */
const SETUP_INCOMPLETE_MESSAGE =
  'Les 6 drivers et 6 leviers de valeur sont requis pour générer une matrice — le reste est optionnel.';

function CompanySpacePage() {
  const { companyId } = Route.useParams();
  // Parsed again: the router merges the raw URL keys that the validated search leaves out
  // (an unknown or default value), and an old point of view must not come back through them.
  const search = parseMatrixGridSearch(Route.useSearch());
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('grid');
  // Set by the creation form when the company was created but its logo could not
  // be saved (issue #20, H11): shown once, then forgotten.
  const [logoWarning] = useState(() => hasLogoWarning(companyId));
  useEffect(() => {
    clearLogoWarning(companyId);
  }, [companyId]);

  const companyQuery = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => companiesApi.getById(companyId),
  });
  // Who may edit the settings (same rule as the backend's
  // `assertCanMutateCompany`). Gates the setup import button; the effective
  // role lets the settings page's role override exercise it.
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => meApi.get() });
  const effectiveRole = useEffectiveRole(meQuery.data);
  const membershipQuery = useQuery({
    queryKey: ['me', 'company'],
    queryFn: () => meApi.getMembership(),
    enabled: effectiveRole === 'admin',
  });
  const canEditSetup = canMutateCompany(effectiveRole, membershipQuery.data?.companyId, companyId);

  function changeGridSearch(next: MatrixGridSearch) {
    // Written here rather than only in `beforeLoad`: going back to the
    // defaults empties the URL, and `beforeLoad` would then restore the
    // previous settings instead of forgetting them.
    storeMatrixGridSearch(companyId, next);
    void navigate({
      to: '/companies/$companyId',
      params: { companyId },
      search: next,
      replace: true,
    });
  }

  if (companyQuery.isLoading) {
    return <p className="text-gray-500 text-small">Chargement…</p>;
  }
  if (companyQuery.isError || !companyQuery.data) {
    return <p className="text-danger text-small">Entreprise introuvable.</p>;
  }
  const company = companyQuery.data;
  // An archived company is closed (issue #20): the notice replaces the whole page.
  if (company.archivedAt) {
    return (
      <ArchivedCompanyNotice companyId={companyId} canRestore={canManageCompanies(effectiveRole)} />
    );
  }

  return (
    <section className="space-y-6 pb-24">
      <div>
        <h1 className="font-bold text-bleu-nuit text-h2">{company.name}</h1>
      </div>

      <div className="flex gap-2 border-gray-200 border-b-2">
        <button
          type="button"
          onClick={() => setTab('grid')}
          className={`px-4 py-2 font-semibold text-small ${
            tab === 'grid'
              ? 'border-bleu-nuit border-b-2 text-bleu-nuit'
              : 'text-gray-500 hover:text-noir'
          }`}
        >
          Matrices
        </button>
        <button
          type="button"
          onClick={() => setTab('settings')}
          className={`px-4 py-2 font-semibold text-small ${
            tab === 'settings'
              ? 'border-bleu-nuit border-b-2 text-bleu-nuit'
              : 'text-gray-500 hover:text-noir'
          }`}
        >
          Paramètres entreprise
        </button>
      </div>

      {logoWarning && <Alert tone="warning">{LOGO_WARNING_MESSAGE}</Alert>}
      {!company.isSetupComplete && <Alert tone="warning">{SETUP_INCOMPLETE_MESSAGE}</Alert>}

      {tab === 'grid' && (
        <MatricesTab
          companyId={companyId}
          companyName={company.name}
          canCreate={company.isSetupComplete}
          search={search}
          onSearchChange={changeGridSearch}
          currentUserId={meQuery.data?.id}
          viewer={
            meQuery.data && effectiveRole ? { id: meQuery.data.id, role: effectiveRole } : undefined
          }
        />
      )}

      {tab === 'settings' && (
        <CompanySettingsPanel company={company} companyId={companyId} canEditSetup={canEditSetup} />
      )}
    </section>
  );
}

interface CompanySettingsPanelProps {
  company: CompanyWithSetup;
  companyId?: string; // optional so unit tests can render the panel without a full route param
  /** Shows « Importer depuis Excel »; off unless the caller knows the user may edit. */
  canEditSetup?: boolean;
}

type SectionKey = 'info' | 'setup' | 'paliers';

/**
 * `CompanyWithSetup` is `Company` PLUS `singularitySetup`/`paliers`/
 * `isSetupComplete` on the same flat object — so assigning `company`
 * straight into `Company`-typed state (as before) carries those three extra
 * runtime keys along for the ride, even though TS's structural typing hides
 * it. That's harmless as long as every `Company`-shaped value in this
 * component comes from the same superset object, but `patchMutation`
 * resolves to the real, narrow `Company` (with no such extras) with no such extras. Comparing the two via
 * `JSON.stringify` would then see a permanent, spurious diff. Narrowing
 * explicitly here keeps every `Company`-shaped value the same actual shape.
 *
 * The logo is left out on purpose (issue #20): its editor saves it on its own,
 * so it must neither make the form dirty nor be carried by « Enregistrer ».
 */
function pickCompanyFields(company: Company): CompanyInfoDraft {
  const { id, name, noyau, contexte, braavoLevel, archivedAt, createdAt, updatedAt } = company;
  return { id, name, noyau, contexte, braavoLevel, archivedAt, createdAt, updatedAt };
}

/**
 * `JSON.stringify` is sensitive to object key insertion order, which makes
 * plain `JSON.stringify(a) !== JSON.stringify(b)` an unreliable dirty check
 * whenever the two sides can be constructed differently — e.g. a local draft
 * built via `{...prev, ...patch}` (which preserves the original insertion
 * order) versus a value coming back from `JSON.parse`-ing a server response
 * (whose key order reflects whatever order the backend happened to
 * serialize its fields in, independent of our client-side construction
 * order). Sorting object keys recursively before stringifying makes the
 * comparison robust to that.
 */
function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, val: unknown) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return Object.keys(val)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = (val as Record<string, unknown>)[key];
          return acc;
        }, {});
    }
    return val;
  });
}

/**
 * Owns every field's draft state for the settings tab, exported separately
 * from `CompanySpacePage` so its dirty-tracking/save-orchestration logic is
 * unit-testable without mounting a full TanStack Router tree.
 */
export function CompanySettingsPanel({
  company,
  companyId,
  canEditSetup = false,
}: CompanySettingsPanelProps) {
  const resolvedCompanyId = companyId ?? company.id;
  const logoHandlers = useStoredCompanyLogo(resolvedCompanyId);
  const queryClient = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  // Shown from « Appliquer » until the setup's next successful save.
  const [importNotice, setImportNotice] = useState<string | null>(null);

  const [infoDraft, setInfoDraft] = useState<CompanyInfoDraft>(() => pickCompanyFields(company));
  const [setupDraft, setSetupDraft] = useState<SingularityLevier[]>(company.singularitySetup);
  const [paliersDraft, setPaliersDraft] = useState<ResolvedPalier[]>(company.paliers);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [sectionErrors, setSectionErrors] = useState<Partial<Record<SectionKey, string>>>({});

  // Per-section "last known persisted" baseline, diffed against the draft to
  // derive dirtiness. Seeded from `company` and re-synced from it below, but
  // ALSO updated optimistically the moment a section's own save succeeds
  // (see `handleSave`) rather than only through the `company` prop. Waiting
  // on the prop alone would tie "is this section clean now?" to the parent's
  // query-cache refetch actually landing (racy, and a no-op in isolation —
  // e.g. under test — when nothing has that query key active), whereas we
  // already know exactly what we just persisted.
  const [savedInfo, setSavedInfo] = useState<CompanyInfoDraft>(() => pickCompanyFields(company));
  const [savedSetup, setSavedSetup] = useState<SingularityLevier[]>(company.singularitySetup);
  const [savedPaliers, setSavedPaliers] = useState<ResolvedPalier[]>(company.paliers);

  const infoDirty = useMemo(
    () => stableStringify(infoDraft) !== stableStringify(savedInfo),
    [infoDraft, savedInfo],
  );
  const setupDirty = useMemo(
    () => stableStringify(setupDraft) !== stableStringify(savedSetup),
    [setupDraft, savedSetup],
  );
  const paliersDirty = useMemo(
    () => stableStringify(paliersDraft) !== stableStringify(savedPaliers),
    [paliersDraft, savedPaliers],
  );

  /**
   * The level the paliers editor resolves "overridden" and "reset" against. At
   * company level that is the 8 global defaults, reshaped into the same
   * `ResolvedPalier` triple the draft uses so the editor never has to know
   * which level it is editing.
   */
  const inheritedPaliers = useMemo<ResolvedPalier[]>(
    () =>
      company.palierDefaults.map((fallback) => ({
        number: fallback.number,
        label: fallback.defaultLabel,
        description: fallback.defaultDescription,
      })),
    [company.palierDefaults],
  );
  const isDirty = infoDirty || setupDirty || paliersDirty;

  // Re-sync drafts (and their saved baseline) whenever the server data
  // changes underneath us (e.g. another admin edited it, or our own save's
  // query invalidation refetched) — but only the sections that are NOT
  // currently dirty, so an in-flight edit is never clobbered.
  useEffect(() => {
    if (infoDirty) return;
    setInfoDraft(pickCompanyFields(company));
    setSavedInfo(pickCompanyFields(company));
  }, [company, infoDirty]);
  useEffect(() => {
    if (setupDirty) return;
    setSetupDraft(company.singularitySetup);
    setSavedSetup(company.singularitySetup);
    // Nothing left to save (saved, or edited back): the import notice is moot.
    setImportNotice(null);
  }, [company.singularitySetup, setupDirty]);
  useEffect(() => {
    if (paliersDirty) return;
    setPaliersDraft(company.paliers);
    setSavedPaliers(company.paliers);
  }, [company.paliers, paliersDirty]);

  // In-app navigation guard. `enableBeforeUnload` is deliberately `false`
  // here: the installed router (@tanstack/react-router ^1.87, resolved to
  // 1.168.23) defaults `enableBeforeUnload` to `true` and — per
  // `@tanstack/history`'s `onBeforeUnload` — that flag is read directly,
  // *without* ever calling `shouldBlockFn`. Leaving it at its default would
  // make the native "leave site?" prompt fire on every tab close/reload
  // regardless of `isDirty`. The browser-level prompt is instead handled by
  // the dedicated `beforeunload` listener below, which does check `isDirty`.
  useBlocker({
    shouldBlockFn: () => {
      if (!isDirty) return false;
      return !window.confirm('Des modifications ne sont pas enregistrées. Quitter quand même ?');
    },
    enableBeforeUnload: false,
  });

  useEffect(() => {
    if (!isDirty) return;
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const patchMutation = useMutation({
    mutationFn: companiesApi.patch.bind(null, resolvedCompanyId),
  });
  const setupMutation = useMutation({
    mutationFn: companiesApi.updateSingularitySetup.bind(null, resolvedCompanyId),
  });
  const paliersMutation = useMutation({
    mutationFn: companiesApi.updatePaliers.bind(null, resolvedCompanyId),
  });

  async function handleSave() {
    setSectionErrors({});
    // Captured by the 'info' job below, if it runs and succeeds — the
    // server's authoritative response, used as the new baseline instead of
    // the client-sent draft (see `setSavedInfo` below: the backend does no
    // normalization today, but should it ever start trimming/normalizing a
    // field, the UI must reflect what was actually persisted, not what was
    // sent).
    let patchedCompany: Company | undefined;
    const jobs: { key: SectionKey; run: () => Promise<unknown> }[] = [];
    if (infoDirty) {
      jobs.push({
        key: 'info',
        run: async () => {
          patchedCompany = await patchMutation.mutateAsync({
            name: infoDraft.name,
            noyau: infoDraft.noyau,
            contexte: infoDraft.contexte,
            braavoLevel: infoDraft.braavoLevel,
          });
          return patchedCompany;
        },
      });
    }
    if (setupDirty) {
      jobs.push({ key: 'setup', run: () => setupMutation.mutateAsync(setupDraft) });
    }
    if (paliersDirty) {
      jobs.push({
        key: 'paliers',
        run: () =>
          paliersMutation.mutateAsync(
            paliersDraft
              .filter((palier) => {
                const saved = savedPaliers.find((entry) => entry.number === palier.number);
                return (
                  palier.label !== saved?.label ||
                  (palier.description ?? null) !== (saved?.description ?? null)
                );
              })
              // A value equal to the inherited one is sent as `null`, not as a
              // copy of it: that is what "Rétablir la valeur par défaut" has to
              // persist, and storing the copy instead would silently pin the
              // palier to today's default the next time the default changes.
              .map((palier) => {
                const base = inheritedPaliers.find((entry) => entry.number === palier.number);
                return {
                  number: palier.number,
                  customLabel: palier.label === base?.label ? null : palier.label,
                  customDescription:
                    (palier.description ?? null) === (base?.description ?? null)
                      ? null
                      : palier.description,
                };
              }),
          ),
      });
    }
    if (jobs.length === 0) return;

    const results = await Promise.allSettled(jobs.map((job) => job.run()));
    const newErrors: Partial<Record<SectionKey, string>> = {};
    let anyFailed = false;
    results.forEach((result, index) => {
      const job = jobs[index];
      if (!job) return;
      if (result.status === 'rejected') {
        anyFailed = true;
        newErrors[job.key] = "Échec de l'enregistrement — réessayez.";
        return;
      }
      // Success: this section's draft IS now the persisted state — advance
      // its baseline immediately rather than waiting for the invalidated
      // query below to eventually refetch and flow back down as a prop.
      // `info` uses the server's actual response (`patchedCompany`) rather
      // than the client-sent draft, since `patchMutation` resolves to the
      // authoritative `Company`; `setup`/`paliers` mutations resolve to
      // `void`, so their draft IS the only value we have for the baseline.
      if (job.key === 'info') {
        setSavedInfo(patchedCompany ? pickCompanyFields(patchedCompany) : infoDraft);
      }
      if (job.key === 'setup') {
        setSavedSetup(setupDraft);
        setImportNotice(null);
      }
      if (job.key === 'paliers') setSavedPaliers(paliersDraft);
    });
    setSectionErrors(newErrors);

    // Still invalidate so other consumers of this company (e.g. the grid
    // tab's `isSetupComplete`-derived matrix-creation gate) pick up the
    // server-computed fields once the refetch lands.
    void queryClient.invalidateQueries({ queryKey: ['company', resolvedCompanyId] });

    if (!anyFailed) {
      setSavedAt(Date.now());
    }
  }

  return (
    <div className="space-y-8">
      {/* The setup-incomplete banner lives at page level (see
          `SETUP_INCOMPLETE_MESSAGE`), not here — it has to sit in the same
          spot on both tabs. */}
      <section className="space-y-3">
        <h2 className="font-semibold text-h4">Informations</h2>
        <CompanyInfoForm
          company={infoDraft}
          onChange={(patch) => setInfoDraft((prev) => ({ ...prev, ...patch }))}
          logoField={{ logo: company.logo, ...logoHandlers }}
        />
        {sectionErrors.info && <p className="text-danger text-small">{sectionErrors.info}</p>}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="font-semibold text-h4">Setup de singularité</h2>
          {canEditSetup && (
            <Button type="button" variant="primary" size="sm" onClick={() => setImportOpen(true)}>
              <Icon name="upload" size={16} />
              Importer depuis Excel
            </Button>
          )}
        </div>
        {importNotice && <Alert tone="info">{importNotice}</Alert>}
        {canEditSetup && (
          <SetupImportDialog
            open={importOpen}
            onOpenChange={setImportOpen}
            companyId={resolvedCompanyId}
            draft={setupDraft}
            noyau={infoDraft.noyau}
            onApply={(applied: AppliedSetupImport) => {
              // Draft only: nothing is persisted before « Enregistrer » (rule 9).
              setSetupDraft(applied.merged);
              setImportNotice(
                `Import appliqué : ${applied.writtenCount} ${
                  applied.writtenCount <= 1 ? 'cellule écrite' : 'cellules écrites'
                } depuis ${applied.sourceLabel}. Pensez à enregistrer.`,
              );
              setImportOpen(false);
            }}
          />
        )}
        <SingularitySetupEditor
          leviers={setupDraft}
          noyau={infoDraft.noyau}
          onChange={setSetupDraft}
        />
        {sectionErrors.setup && <p className="text-danger text-small">{sectionErrors.setup}</p>}
      </section>

      <section className="space-y-3">
        <h2 className="font-semibold text-h4">Paliers</h2>
        <p className="max-w-[46rem] text-gray-600 text-small">
          Les 8 paliers sont fixes. Le libellé est l’en-tête de colonne ; la description est la
          définition du palier fournie au modèle lors de la génération. Les deux sont facultatifs :
          sans surcharge, le palier garde sa valeur globale.
        </p>
        <PaliersEditor
          paliers={paliersDraft}
          inherited={inheritedPaliers}
          resetLabel="Rétablir la valeur par défaut"
          overriddenLegend="personnalisé pour cette entreprise"
          onChange={setPaliersDraft}
        />
        {sectionErrors.paliers && <p className="text-danger text-small">{sectionErrors.paliers}</p>}
      </section>

      <div className="fixed inset-x-0 bottom-0 flex items-center justify-end gap-3 border-gray-200 border-t-2 bg-white px-6 py-4">
        {savedAt && !isDirty && <span className="text-small text-success">Enregistré</span>}
        <Button
          variant="secondary"
          type="button"
          disabled={
            !isDirty ||
            patchMutation.isPending ||
            setupMutation.isPending ||
            paliersMutation.isPending
          }
          onClick={() => void handleSave()}
        >
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
